import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, PencilSimple, Clock } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Session-49 — Rewritten as a two-column Old ↔ New preview.
 *
 * • No edit limit — admin edits as many times as needed. Each edit is
 *   stored in `edit_history`; the 3-dot menu grows a "View Edit Previews"
 *   option so past changes remain auditable.
 * • Two visual columns:
 *      LEFT  ▸ पुरानी Value  (read-only snapshot from server)
 *      RIGHT ▸ नई Value      (editable inputs)
 *   On mobile these stack (Old on top, New below) via responsive grid.
 * • Fields editable for BOTH deposit + withdraw:
 *      - amount, utr_number, description, bank
 *      - withdraw also: reference, paid_reference, paid_bank_account
 * • Save Changes button is disabled until at least one field differs.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - type: 'deposit' | 'withdraw'
 *   - entryId: string
 *   - actorId: current user id
 *   - onEdited: (entry) => void   // called after successful save
 *   - showPaidFields?: boolean    // withdraw only: expose UTR / paid_*
 */
const EditEntryModal = ({ open, onClose, type, entryId, actorId, onEdited, showPaidFields, openHistoryOnMount }) => {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Form state (RIGHT column — editable "new value")
  const [amount, setAmount] = useState('');
  const [utr, setUtr] = useState('');
  const [description, setDescription] = useState('');
  const [bank, setBank] = useState('');
  const [reference, setReference] = useState('');
  const [paidReference, setPaidReference] = useState('');
  const [paidBank, setPaidBank] = useState('');

  const url = useMemo(() => {
    if (type === 'deposit') return `${API}/deposit-entries/${entryId}`;
    return `${API}/withdraw-entries/single/${entryId}`;
  }, [type, entryId]);

  // Load entry once per (type, entryId). Deliberately do NOT depend on `open`
  // or `onClose` — those change on every parent re-render and would trigger a
  // fetch loop while the parent (chat page) polls messages.
  useEffect(() => {
    if (!entryId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(url)
      .then((res) => {
        if (cancelled) return;
        const e = res.data || {};
        setEntry(e);
        setAmount(String(e.amount ?? ''));
        setUtr(e.utr_number || '');
        setDescription(e.description || '');
        setBank(e.bank_account || '');
        setReference(e.reference || '');
        setPaidReference(e.paid_reference || '');
        setPaidBank(e.paid_bank_account || '');
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err?.response?.data?.detail || 'Failed to load entry');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, entryId]);

  // Session-49: when opened from the "View Edit Previews" menu item, jump
  // straight into the history overlay so the user doesn't have to look for
  // the small "View edit history" link.
  useEffect(() => {
    if (openHistoryOnMount && entry && (entry.edit_history || []).length > 0) {
      setShowHistory(true);
    }
  }, [openHistoryOnMount, entry]);

  const editCount = Number(entry?.edit_count || 0);

  // Build the diff row list — decides which fields will actually change on save.
  const fieldRows = useMemo(() => {
    if (!entry) return [];
    const rows = [
      { key: 'amount', label: 'Amount (₹)', oldVal: entry.amount, newVal: amount, setter: setAmount,
        numeric: true, required: true },
    ];
    if (type === 'deposit') {
      rows.push(
        { key: 'utr_number', label: 'UTR Number', oldVal: entry.utr_number, newVal: utr, setter: setUtr },
        { key: 'description', label: 'Description', oldVal: entry.description, newVal: description, setter: setDescription },
        { key: 'bank_account', label: 'Bank Account', oldVal: entry.bank_account, newVal: bank, setter: setBank },
      );
    } else {
      rows.push(
        { key: 'reference', label: 'Reference', oldVal: entry.reference, newVal: reference, setter: setReference },
      );
      if (showPaidFields || entry.status === 'paid') {
        rows.push(
          { key: 'utr_number', label: 'UTR Number', oldVal: entry.utr_number, newVal: utr, setter: setUtr },
          { key: 'paid_reference', label: 'Paid Reference', oldVal: entry.paid_reference, newVal: paidReference, setter: setPaidReference },
          { key: 'paid_bank_account', label: 'Paid Bank Account', oldVal: entry.paid_bank_account, newVal: paidBank, setter: setPaidBank },
        );
      }
    }
    return rows;
  }, [entry, type, amount, utr, description, bank, reference, paidReference, paidBank, showPaidFields]);

  const dirty = fieldRows.some(r => String(r.newVal ?? '').trim() !== String(r.oldVal ?? '').trim());

  const save = async () => {
    if (!dirty) { toast.message('No changes to save'); return; }
    setSaving(true);
    try {
      const patchUrl = type === 'deposit'
        ? `${API}/deposit-entries/${entryId}`
        : `${API}/withdraw-entries/${entryId}`;
      const body = { actor_id: actorId };
      fieldRows.forEach(r => {
        if (String(r.newVal ?? '').trim() !== String(r.oldVal ?? '').trim()) {
          body[r.key] = r.newVal;
        }
      });
      const res = await axios.patch(patchUrl, body);
      toast.success('✓ Entry updated · customer ko notification bhej di gayi');
      onEdited && onEdited(res.data?.entry);
      onClose && onClose();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Save failed');
    } finally { setSaving(false); }
  };

  if (!open) return null;

  const accent = type === 'deposit' ? '#3b82f6' : '#f59e0b';

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      data-testid="edit-entry-modal"
    >
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: accent }}>
          <div className="text-white font-bold text-sm uppercase flex items-center gap-2">
            <PencilSimple size={16} weight="bold" />
            Edit {type === 'deposit' ? 'Deposit' : 'Withdraw'}
            {editCount > 0 && (
              <span className="ml-2 text-[11px] bg-white/25 px-2 py-0.5 rounded normal-case font-normal">
                {editCount} previous edit{editCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="edit-entry-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="p-4 overflow-y-auto">
            {/* Column headers */}
            <div className="hidden sm:grid sm:grid-cols-2 gap-3 mb-2 text-[10px] uppercase tracking-wider">
              <div className="text-slate-500 font-bold">पुरानी Value</div>
              <div className="font-bold" style={{ color: accent }}>नई Value</div>
            </div>

            <div className="space-y-3">
              {fieldRows.map((r) => {
                const changed = String(r.newVal ?? '').trim() !== String(r.oldVal ?? '').trim();
                return (
                  <div key={r.key} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* OLD (read-only snapshot) */}
                    <div>
                      <label className="text-[10px] text-slate-500 mb-1 block font-semibold uppercase sm:hidden">
                        पुरानी {r.label}
                      </label>
                      <div className="px-3 py-2 border border-slate-200 bg-slate-50 rounded text-sm text-slate-600 break-words min-h-[38px]">
                        {r.oldVal === null || r.oldVal === undefined || r.oldVal === ''
                          ? <span className="italic text-slate-400">(empty)</span>
                          : String(r.oldVal)}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5 hidden sm:block">{r.label}</div>
                    </div>
                    {/* NEW (editable) */}
                    <div>
                      <label className="text-[10px] text-slate-500 mb-1 block font-semibold uppercase sm:hidden">
                        नई {r.label}
                      </label>
                      <input
                        type="text"
                        inputMode={r.numeric ? 'numeric' : 'text'}
                        value={r.newVal || ''}
                        onChange={(e) => {
                          const v = r.numeric ? e.target.value.replace(/[^0-9,]/g, '') : e.target.value;
                          r.setter(v);
                        }}
                        className={`w-full px-3 py-2 border rounded text-sm min-h-[38px] ${
                          changed
                            ? 'border-amber-400 bg-amber-50 font-semibold'
                            : 'border-slate-300 bg-white'
                        }`}
                        data-testid={`edit-entry-${r.key}`}
                      />
                      <div className={`text-[10px] mt-0.5 hidden sm:block ${changed ? 'text-amber-700 font-medium' : 'text-slate-400'}`}>
                        {r.label}{changed ? ' · will change' : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {editCount > 0 && (
              <button
                onClick={() => setShowHistory(true)}
                className="mt-4 w-full flex items-center justify-center gap-2 py-2 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded border border-slate-200"
                data-testid="edit-entry-history-btn"
              >
                <Clock size={14} /> View edit history ({editCount})
              </button>
            )}

            <div className="flex gap-2 pt-4">
              <button
                onClick={onClose}
                disabled={saving}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving || !dirty}
                data-kb-submit
                className="flex-1 py-2 rounded-lg text-white font-bold text-sm shadow disabled:opacity-40"
                style={{ background: accent }}
                data-testid="edit-entry-save-btn"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </div>

      {showHistory && entry && (
        <EditHistoryOverlay entry={entry} onClose={() => setShowHistory(false)} />
      )}
    </div>
  );
};

const _fmtVal = (v) => (v === null || v === undefined || v === ''
  ? <span className="text-gray-400 italic">(empty)</span>
  : String(v));

const EditHistoryOverlay = ({ entry, onClose }) => {
  const history = entry.edit_history || [];
  return (
    <div
      className="fixed inset-0 bg-black/70 z-[90] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="edit-history-overlay"
    >
      <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 bg-slate-800 text-white font-bold text-sm flex items-center justify-between">
          <span>Edit History · {history.length}</span>
          <button onClick={onClose} className="text-white/80 hover:text-white"><X size={18} weight="bold" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto text-sm">
          {history.length === 0 ? (
            <div className="text-gray-500 text-center py-6 text-xs">No edits yet</div>
          ) : (
            history.map((h, idx) => (
              <div key={idx} className="border rounded-lg p-3 space-y-2" data-testid={`edit-history-row-${idx}`}>
                <div className="text-[11px] text-gray-500">
                  <b>Edit #{idx + 1}</b> · {String(h.at || '').slice(0, 16).replace('T', ' ')} · by {h.actor_display || h.actor_username || '-'}
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 text-left">
                      <th className="py-1 font-medium">Field</th>
                      <th className="py-1 font-medium">Before</th>
                      <th className="py-1 font-medium">After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(h.next || {}).map((k) => (
                      <tr key={k} className="border-t border-slate-100">
                        <td className="py-1 pr-2 font-semibold text-slate-700">{k}</td>
                        <td className="py-1 pr-2 text-red-600 line-through">{_fmtVal(h.prev?.[k])}</td>
                        <td className="py-1 text-emerald-700 font-medium">{_fmtVal(h.next?.[k])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EditEntryModal;
export { EditHistoryOverlay };
